import { EnterOutlined, LoadingOutlined } from '@ant-design/icons'
import { Table, Layout, EmptyState } from '@loadsmart/miranda-react'
import type { PaginationChangeEventDetails } from '@loadsmart/miranda-react'
import { Spin, Pagination } from 'antd/lib'
import { useCallback, useState } from 'react'
import styled from 'styled-components'

import type { GateFeedFilter, ObservationSort } from '../../../clients/type'
import type { SortingAction } from '../../../models/client/observation'
import type { Gate, Location, TableColumn, ViewableImage } from '../../../models/client/reports'
import { getRowValue } from '../../utils/reports'

import { TableDrawer } from './ReportTableDrawer'
import { TableFilters } from './ReportTableFilters'
import { getActiveBigNumberFilter } from '../../../clients/tools'
import { usePersistedState } from '../../../hooks/usePersistedState'
import { RenderImage } from '../RenderImage'
import { TableFiltersEnhanced } from './ReportTableFiltersEnhanced'
import analytics, { AnalyticsEvent, AnalyticsEventTrigger } from '../../../util/analytics'

type TableProps = {
  data: any
  gates?: Gate[]
  filters: GateFeedFilter[]
  page: number
  totalItems: number
  totalAssets?: number
  totalPages: number
  selectedLocation: Location
  selectedGate?: Gate
  selectedCategory: any
  handlePageChange: (pageIndex: number) => void
  handleSearchCategoryChange: (category) => void
  handleGateChange?: (gate: any) => void
  onAddFilter: (filter: GateFeedFilter) => void
  onRemoveFilter: (filter: GateFeedFilter) => void
  onRemovePropertyFilters?: () => void
  onOpenAsset: (assetId: string) => void
  onViewImage?: (image: ViewableImage) => void
  onResetFilters: () => void
  isLoading: boolean
  setSort: (action: SortingAction) => void
  sort: ObservationSort | null
  columns: TableColumn[]
  columnOptions: TableColumn[]
  enhancedFilters?: boolean
}

const StyledStack = styled(Layout.Stack)`
  height: 62vh;
  justify-content: center;
`

const StyledEnterOutlined = styled(EnterOutlined)`
  padding-left: 5px;
  transform: scaleX(-1);
`

const StyledHeadCell = styled(Table.HeadCell)`
  min-width: 130px;
`

const StyledPagination = styled(Pagination)`
  padding: 10px;
  .ant-pagination-item-active {
    border-color: rgba(var(--m-color-primary-60, 0, 84, 84), 1);
    &:hover {
      border-color: rgba(var(--m-color-primary-60, 0, 84, 84), 1);
    }
  }
  .ant-pagination-item-active a {
    color: rgba(var(--m-color-primary-60, 0, 84, 84), 1);
    &:hover {
      color: rgba(var(--m-color-primary-60, 0, 84, 84), 1);
    }
  }
`

export function ReportTable({
  isLoading,
  data,
  gates,
  filters,
  page,
  totalItems,
  totalAssets,
  selectedLocation,
  selectedCategory,
  selectedGate,
  handlePageChange,
  handleSearchCategoryChange,
  handleGateChange,
  onAddFilter,
  onRemoveFilter,
  onRemovePropertyFilters,
  onResetFilters,
  onOpenAsset,
  onViewImage,
  setSort,
  sort,
  columns,
  columnOptions,
  enhancedFilters,
}: TableProps) {
  const { value: storedColumns, setValue: storeColumns } = usePersistedState<TableColumn[]>(
    enhancedFilters ? 'NT_GATE_FEED_TOOLS' : 'NT_TOOLS',
    []
  )

  const initialColumns = storedColumns && storedColumns.length ? storedColumns : columns

  const [open, setOpen] = useState(false)
  const [activeColumns, setActiveColumns] = useState<TableColumn[]>(initialColumns)

  const hasEnhancedFilters = enhancedFilters && gates && selectedGate && handleGateChange

  const handleColumnChange = (columns: TableColumn[]) => {
    setActiveColumns(columns)
    storeColumns(columns)
  }

  const handleOpenDrawer = () => {
    setOpen(true)
    analytics.track(AnalyticsEvent.ReportsTableSettings, AnalyticsEventTrigger.click)
  }

  const handleCloseDrawer = () => {
    setOpen(false)
  }

  const handlePagination = (page: number) => {
    handlePageChange(page - 1)
  }

  const getSortDirection = useCallback(
    (column: TableColumn) => {
      if (sort?.column === column.value) {
        return sort.direction === 'ASC' ? 'asc' : sort.direction === 'DESC' ? 'desc' : undefined
      }
      return undefined
    },
    [sort]
  )

  const handleSort = (column: TableColumn) => {
    let direction: 'ASC' | 'DESC' | undefined = 'DESC'

    if (sort?.column === column.value) {
      if (sort.direction === 'DESC') {
        direction = 'ASC'
      } else if (sort.direction === 'ASC') {
        direction = undefined
      }
    }

    setSort({ column: column.value, direction })
  }

  const shouldDisplayAsAttachment = (row, column) => {
    const activeBigNumberFilter = getActiveBigNumberFilter(filters)
    return column.value === 'assetType' && row.isAttachment && !activeBigNumberFilter
  }

  if (!isLoading && data?.length === 0) {
    return (
      <Table>
        {hasEnhancedFilters ? (
          <TableFiltersEnhanced
            handleOpenDrawer={handleOpenDrawer}
            selectedLocation={selectedLocation}
            selectedCategory={selectedCategory}
            selectedGate={selectedGate}
            handleSearchCategoryChange={handleSearchCategoryChange}
            handleGateChange={handleGateChange}
            filters={filters}
            gates={gates}
            onAddFilter={onAddFilter}
            onRemoveFilter={onRemoveFilter}
            onRemovePropertyFilters={onRemovePropertyFilters}
            onResetFilters={onResetFilters}
            isLoading={isLoading}
          />
        ) : (
          <TableFilters
            handleOpenDrawer={handleOpenDrawer}
            selectedLocation={selectedLocation}
            selectedCategory={selectedCategory}
            handleSearchCategoryChange={handleSearchCategoryChange}
            filters={filters}
            onAddFilter={onAddFilter}
            onRemoveFilter={onRemoveFilter}
            onResetFilters={onResetFilters}
            isLoading={isLoading}
          />
        )}

        <Table.Feedback>
          <Layout.Group justify="center" paddingY="spacing-6">
            <EmptyState variant="page">
              <EmptyState.Header>No results available</EmptyState.Header>
              <EmptyState.Message>Please try again later</EmptyState.Message>
            </EmptyState>
          </Layout.Group>
        </Table.Feedback>
      </Table>
    )
  }

  return (
    <>
      <Table>
        {hasEnhancedFilters ? (
          <TableFiltersEnhanced
            handleOpenDrawer={handleOpenDrawer}
            selectedLocation={selectedLocation}
            selectedCategory={selectedCategory}
            selectedGate={selectedGate}
            handleSearchCategoryChange={handleSearchCategoryChange}
            handleGateChange={handleGateChange}
            filters={filters}
            gates={gates}
            onAddFilter={onAddFilter}
            onRemoveFilter={onRemoveFilter}
            onRemovePropertyFilters={onRemovePropertyFilters}
            onResetFilters={onResetFilters}
            isLoading={isLoading}
          />
        ) : (
          <TableFilters
            handleOpenDrawer={handleOpenDrawer}
            selectedLocation={selectedLocation}
            selectedCategory={selectedCategory}
            handleSearchCategoryChange={handleSearchCategoryChange}
            filters={filters}
            onAddFilter={onAddFilter}
            onRemoveFilter={onRemoveFilter}
            onResetFilters={onResetFilters}
            isLoading={isLoading}
          />
        )}

        {!data || isLoading ? (
          <Table.Feedback>
            <StyledStack>
              <Layout.Group justify="center" paddingY="spacing-6">
                <EmptyState variant="page">
                  <EmptyState.Message>
                    <Layout.Stack>
                      <Spin indicator={<LoadingOutlined spin />} />
                      Loading assets...
                    </Layout.Stack>
                  </EmptyState.Message>
                </EmptyState>
              </Layout.Group>
            </StyledStack>
          </Table.Feedback>
        ) : (
          <>
            <Table.Head>
              <Table.Row>
                {activeColumns.map((column) => (
                  <StyledHeadCell
                    key={column.value}
                    sortable={column.sortable}
                    onClick={() => (column.sortable ? handleSort(column) : null)}
                    sortDirection={getSortDirection(column)}
                  >
                    {column.label}
                  </StyledHeadCell>
                ))}
              </Table.Row>
            </Table.Head>

            <Table.Body>
              {data.map((row) => (
                <>
                  <Table.Row key={row.id} onClick={() => onOpenAsset(row.id)}>
                    {activeColumns.map((column) => (
                      <Table.Cell key={column.value} style={{ cursor: 'pointer' }}>
                        {shouldDisplayAsAttachment(row, column) ? <StyledEnterOutlined /> : null}
                        {column.value === 'assetImagePaths' ? (
                          <RenderImage observation={row} onViewImage={onViewImage} />
                        ) : (
                          getRowValue(column.value, row)
                        )}
                      </Table.Cell>
                    ))}
                  </Table.Row>
                </>
              ))}
            </Table.Body>
          </>
        )}

        <Table.Actions sticky>
          <StyledPagination
            total={totalAssets || totalItems}
            showTotal={() => `${totalItems} items`}
            pageSize={50}
            current={page + 1}
            onChange={handlePagination}
            disabled={isLoading}
            showSizeChanger={false}
          />
        </Table.Actions>
      </Table>

      <TableDrawer
        open={open}
        handleClose={handleCloseDrawer}
        columns={columnOptions}
        activeColumns={activeColumns}
        setActiveColumns={handleColumnChange}
      />
    </>
  )
}
